import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { KeyboardEvent } from 'react';
import { graphql } from 'gatsby';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import * as styles from './feature-showcase-slider.module.css';
import {
    KontentStringValue,
    KontentRichText,
    KontentMedia,
    BrandColor,
    CTAConfiguration,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';
import { RichTextEl } from '../Global/RichText';
import { getColor, getMultiChoice } from '../Global/DataUtils';
import { useIsClient } from '../Global/DataUtils/useIsClient';
import { isRichTextFieldBlank } from '../../global/utility';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import ArrowLgButton from '../Global/ArrowLgButton';
import LazyImage from '../Global/LazyImage';
import type { Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import {
    AUTO_ADVANCE_MS,
    MOBILE_SLIDE_MS,
    DEFAULT_GRADIENT_END,
    DEFAULT_GRADIENT_START,
    mergeFeatureShowcaseHeadlineLines,
    normalizeFeatureShowcaseSlides,
    type NormalizedSlide,
} from './featureShowcaseSliderUtils';

type YesNo = 'yes' | 'no';

export interface FeatureShowcaseSliderProps {
    headline_line_1?: KontentRichText;
    headline_line_2?: KontentRichText;
    headline_line_3?: KontentRichText;
    description?: KontentRichText;
    categories?: KontentMedia;
    categories_background_color?: BrandColor;
    categories_font_color?: BrandColor;
    cta_configuration__cta_label?: KontentStringValue;
    cta_configuration__cta_location?: CTAConfiguration['cta_configuration__cta_location'];
    cta_configuration__cta_location_anchor_point?: KontentStringValue;
    cta_configuration__cta_window_action?: CTAConfiguration['cta_configuration__cta_window_action'];
    auto_scroll?: KontentRadioMultiChoice<YesNo>;
    gradient_background?: KontentRadioMultiChoice<YesNo>;
    background_color_1?: BrandColor;
    background_color_2?: BrandColor;
    font_color?: BrandColor;
    anchor_id?: KontentStringValue;
    kontentItemId?: string;
}

const FeatureShowcaseSlider: React.FC<FeatureShowcaseSliderProps> = ({
    headline_line_1,
    headline_line_2,
    headline_line_3,
    description,
    categories,
    categories_background_color,
    categories_font_color,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_location_anchor_point,
    cta_configuration__cta_window_action,
    auto_scroll,
    gradient_background,
    background_color_1,
    background_color_2,
    font_color,
    anchor_id,
    kontentItemId,
}) => {
    const breakpoints = useBreakpoint();
    const isClient = useIsClient();
    const baseId = useId().replace(/:/g, '');
    const slidePanelId = `${baseId}-slide-panel`;

    const slides = useMemo(
        () => normalizeFeatureShowcaseSlides(categories),
        [categories]
    );

    const [activeIndex, setActiveIndex] = useState(0);
    const [userPausedInteraction, setUserPausedInteraction] = useState(false);
    const [isHoveringDesktop, setIsHoveringDesktop] = useState(false);
    const mobileSwiperRef = useRef<SwiperClass | null>(null);

    const slideCount = slides.length;

    useEffect(() => {
        if (activeIndex >= slideCount && slideCount > 0) {
            setActiveIndex(0);
        }
    }, [activeIndex, slideCount]);

    const autoScrollOn =
        (getMultiChoice(auto_scroll) || '').toLowerCase() === 'yes';
    const gradientOn =
        (getMultiChoice(gradient_background) || '').toLowerCase() === 'yes';

    const bg1 = getColor(background_color_1) || DEFAULT_GRADIENT_START;
    const bg2 = getColor(background_color_2) || DEFAULT_GRADIENT_END;
    const textColor = getColor(font_color) || '#ffffff';

    const sectionBackground = useMemo(() => {
        if (gradientOn) {
            return `linear-gradient(180deg, ${bg1} 0%, ${bg2} 100%)`;
        }
        return bg1;
    }, [gradientOn, bg1, bg2]);

    const ctaHref = BuildLinkHref(
        cta_configuration__cta_location,
        cta_configuration__cta_location_anchor_point
    );
    const ctaWindowAction = getMultiChoice(
        cta_configuration__cta_window_action
    );
    const ctaLabel = cta_configuration__cta_label?.value;
    const anchorPoint = cta_configuration__cta_location_anchor_point?.value;

    const isLargeViewport = breakpoints.xl;
    const pauseRotation =
        userPausedInteraction || (isLargeViewport && isHoveringDesktop);

    const pauseRotationRef = useRef(pauseRotation);
    pauseRotationRef.current = pauseRotation;
    const isXlRef = useRef(isLargeViewport);
    isXlRef.current = isLargeViewport;

    useEffect(() => {
        if (!autoScrollOn || slideCount < 2) {
            return undefined;
        }
        const id = window.setInterval(() => {
            if (isXlRef.current) return;
            if (pauseRotationRef.current) return;
            setActiveIndex((i) => (i + 1) % slideCount);
        }, AUTO_ADVANCE_MS);
        return () => window.clearInterval(id);
    }, [autoScrollOn, slideCount]);

    useEffect(() => {
        if (isLargeViewport || slideCount < 1) return;
        const swiper = mobileSwiperRef.current;
        if (!swiper || swiper.destroyed) return;
        if (swiper.activeIndex === activeIndex) return;
        if (swiper.animating) return;
        try {
            swiper.slideTo(activeIndex, MOBILE_SLIDE_MS, false);
        } catch {
            return;
        }
    }, [activeIndex, isLargeViewport, slideCount]);

    const goPrev = useCallback(() => {
        setUserPausedInteraction(true);
        setActiveIndex((i) => (i - 1 + slideCount) % slideCount);
    }, [slideCount]);

    const goNext = useCallback(() => {
        setUserPausedInteraction(true);
        setActiveIndex((i) => (i + 1) % slideCount);
    }, [slideCount]);

    const selectTab = useCallback((index: number) => {
        setUserPausedInteraction(true);
        setActiveIndex(index);
    }, []);

    const onTabListKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (slideCount < 2) return;
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                goNext();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goPrev();
            } else if (e.key === 'Home') {
                e.preventDefault();
                selectTab(0);
            } else if (e.key === 'End') {
                e.preventDefault();
                selectTab(slideCount - 1);
            }
        },
        [slideCount, goNext, goPrev, selectTab]
    );

    const currentSlide: NormalizedSlide | undefined = slides[activeIndex];
    const hasValidSlide = slideCount > 0 && Boolean(currentSlide?.imageUrl);

    const sectionId = anchor_id?.value || undefined;

    const tabBgActive = getColor(categories_background_color) || '#ffffff';
    const tabFgActive = getColor(categories_font_color) || '#1a1816';
    const tablistStyle = {
        '--fss-tab-active-bg': tabBgActive,
        '--fss-tab-active-fg': tabFgActive,
        '--fss-tab-active-hover-bg': '#f5f5f5',
        '--fss-tab-active-hover-border': '#f5f5f5',
    } as React.CSSProperties;

    const mergedHeadline = useMemo(
        () =>
            mergeFeatureShowcaseHeadlineLines(
                headline_line_1,
                headline_line_2,
                headline_line_3
            ),
        [headline_line_1, headline_line_2, headline_line_3]
    );

    const renderHeadline = () =>
        mergedHeadline && !isRichTextFieldBlank(mergedHeadline.value) ? (
            <div className={styles.headline} style={{ color: textColor }}>
                <RichTextEl
                    richText={mergedHeadline}
                    wrapperClassName={styles.richTextRoot}
                    elementCodename="headline_line_1"
                />
            </div>
        ) : null;

    const renderDescription = () =>
        description && !isRichTextFieldBlank(description.value) ? (
            <div className={styles.description} style={{ color: textColor }}>
                <RichTextEl
                    richText={description}
                    wrapperClassName={styles.richTextRoot}
                    elementCodename="description"
                />
            </div>
        ) : null;

    const renderCta = () =>
        ctaHref && ctaLabel ? (
            <div className={styles.ctaWrap}>
                <CtaLink
                    href={ctaHref}
                    target={
                        ctaWindowAction === 'new_window' ? '_blank' : undefined
                    }
                    visualStyle="outlineLight"
                    id={anchorPoint}
                >
                    {ctaLabel}
                </CtaLink>
            </div>
        ) : null;

    const renderTablist = () => (
        <div
            className={styles.tablist}
            role="tablist"
            aria-label="Product categories"
            onKeyDown={onTabListKeyDown}
            style={tablistStyle}
        >
            {slides.map((slide, index) => {
                const selected = index === activeIndex;
                const tabId = `${baseId}-tab-${index}`;
                return (
                    <button
                        key={slide.id}
                        type="button"
                        role="tab"
                        id={tabId}
                        aria-selected={selected}
                        aria-controls={slidePanelId}
                        tabIndex={selected ? 0 : -1}
                        className={`${styles.tab} ${
                            selected ? styles.tabActive : styles.tabInactive
                        }`}
                        onClick={() => selectTab(index)}
                        onFocus={() => setUserPausedInteraction(true)}
                        data-kontent-element-codename="caption"
                    >
                        {slide.label}
                    </button>
                );
            })}
        </div>
    );

    const renderDesktopSlidePanel = () => (
        <div
            className={styles.imageFrame}
            id={slidePanelId}
            role="tabpanel"
            aria-labelledby={`${baseId}-tab-${activeIndex}`}
            aria-live="polite"
            data-kontent-element-codename="categories"
        >
            <LazyImage
                priority
                key={currentSlide.id}
                className={styles.slideImage}
                src={currentSlide.imageUrl}
                srcSet={currentSlide.imageSrcSet}
                sizes="(max-width: 1199px) min(36rem, calc(100vw - 8.5rem)), 748px"
                alt={currentSlide.alt || currentSlide.label}
                width={currentSlide.imageWidth ?? 748}
                height={currentSlide.imageHeight ?? 748}
            />
        </div>
    );

    const renderMobileSwiper = () => (
        <div className={styles.imageFrame}>
            <Swiper
                className={styles.mobileSwiper}
                wrapperClass={styles.mobileSwiperWrapper}
                slidesPerView={1}
                spaceBetween={0}
                speed={MOBILE_SLIDE_MS}
                initialSlide={activeIndex}
                allowTouchMove={slideCount > 1}
                simulateTouch
                touchEventsTarget="container"
                touchRatio={1}
                threshold={4}
                touchAngle={55}
                followFinger
                nested
                touchReleaseOnEdges
                resistanceRatio={0.65}
                onSwiper={(swiper) => {
                    mobileSwiperRef.current = swiper;
                }}
                onSlideChange={(swiper) => {
                    setActiveIndex(swiper.activeIndex);
                }}
                onTouchStart={() => setUserPausedInteraction(true)}
                onBeforeDestroy={() => {
                    mobileSwiperRef.current = null;
                }}
            >
                {slides.map((slide, index) => (
                    <SwiperSlide
                        key={slide.id}
                        className={styles.mobileSlide}
                        data-kontent-element-codename="categories"
                    >
                        <LazyImage
                            priority={index === 0}
                            className={styles.slideImage}
                            src={slide.imageUrl}
                            srcSet={slide.imageSrcSet}
                            sizes="(max-width: 1199px) min(36rem, calc(100vw - 8.5rem)), 748px"
                            alt={slide.alt || slide.label}
                            width={slide.imageWidth ?? 748}
                            height={slide.imageHeight ?? 748}
                            draggable={false}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );

    const arrowStroke = textColor;

    if (!hasValidSlide) {
        return (
            <section
                className={styles.section}
                id={sectionId}
                style={{
                    background: sectionBackground,
                    color: textColor,
                }}
                role="status"
                data-kontent-item-id={kontentItemId}
            >
                <div className={styles.inner}>
                    <p className={styles.fallbackMessage}>
                        Feature showcase content is missing or incomplete.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section
            className={styles.section}
            id={sectionId}
            style={{
                background: sectionBackground,
                color: textColor,
            }}
            onMouseEnter={() => isLargeViewport && setIsHoveringDesktop(true)}
            onMouseLeave={() => setIsHoveringDesktop(false)}
            data-kontent-item-id={kontentItemId}
        >
            <div className={styles.inner}>
                {(!isClient || !isLargeViewport) && (
                    <div className={styles.mobile}>
                        {renderHeadline()}
                        <div className={styles.imageWithArrows}>
                            <ArrowLgButton
                                direction="left"
                                strokeColor={arrowStroke}
                                className={styles.arrowLeft}
                                handleClick={goPrev}
                                aria-label="Previous slide"
                            />
                            {renderMobileSwiper()}
                            <ArrowLgButton
                                direction="right"
                                strokeColor={arrowStroke}
                                className={styles.arrowRight}
                                handleClick={goNext}
                                aria-label="Next slide"
                            />
                        </div>
                        {renderDescription()}
                        {renderCta()}
                    </div>
                )}

                {(!isClient || isLargeViewport) && (
                    <div className={styles.desktop}>
                        <div
                            className={`${styles.imageCol} ${styles.desktopImageCol}`}
                        >
                            {renderDesktopSlidePanel()}
                        </div>
                        <div className={styles.contentCol}>
                            {renderHeadline()}
                            {renderDescription()}
                            {renderTablist()}
                            {renderCta()}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default FeatureShowcaseSlider;

export const fragmentFeatureShowcaseSlider = graphql`
    fragment FeatureShowcaseSlider on kontent_item_component___feature_showcase_slider {
        elements {
            headline_line_1 {
                ...kontentRichText
            }
            headline_line_2 {
                ...kontentRichText
            }
            headline_line_3 {
                ...kontentRichText
            }
            description {
                ...kontentRichText
            }
            categories {
                ...kontentImage
            }
            categories_background_color {
                ...kontentColor
            }
            categories_font_color {
                ...kontentColor
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                    name
                }
            }
            auto_scroll {
                value {
                    codename
                }
            }
            gradient_background {
                value {
                    codename
                }
            }
            background_color_1 {
                ...kontentColor
            }
            background_color_2 {
                ...kontentColor
            }
            font_color {
                ...kontentColor
            }
            anchor_id {
                value
            }
        }
    }
`;
